import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Critical Path 3D Network
 * - Spherical nodes representing project milestones
 * - Glowing edges (electric blue) with a critical path (amber)
 * - Camera slowly orbits and is influenced by scroll progress (via prop)
 */

const NODES = [
  { id: 0, pos: [-4.5, 1.2, 0], critical: true },
  { id: 1, pos: [-2.2, 2.4, -1.2], critical: true },
  { id: 2, pos: [-2.0, -1.0, 1.0], critical: false },
  { id: 3, pos: [0.4, 1.6, 0.6], critical: true },
  { id: 4, pos: [0.2, -1.8, -0.8], critical: false },
  { id: 5, pos: [2.4, 0.4, 1.4], critical: true },
  { id: 6, pos: [2.6, -2.0, -0.4], critical: false },
  { id: 7, pos: [4.6, 1.0, 0.2], critical: true },
  { id: 8, pos: [4.2, -1.4, 1.6], critical: false },
];

const EDGES = [
  [0, 1, true],
  [0, 2, false],
  [1, 3, true],
  [2, 4, false],
  [3, 5, true],
  [2, 5, false],
  [4, 6, false],
  [5, 7, true],
  [6, 8, false],
  [5, 8, false],
  [3, 4, false],
  [7, 8, false],
];

function Node({ position, critical }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const s =
      1 + Math.sin(t * 1.4 + position[0] + position[1]) * (critical ? 0.18 : 0.08);
    ref.current.scale.setScalar(s);
  });
  const color = critical ? "#FFB300" : "#00E5FF";
  return (
    <group position={position}>
      <mesh ref={ref}>
        <sphereGeometry args={[critical ? 0.16 : 0.11, 24, 24]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh>
        <sphereGeometry args={[critical ? 0.32 : 0.22, 16, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.12} />
      </mesh>
    </group>
  );
}

function Edge({ a, b, critical }) {
  const ref = useRef();
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(...a),
      new THREE.Vector3(...b),
    ]);
    return g;
  }, [a, b]);
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.material.opacity = critical
      ? 0.75 + Math.sin(t * 2) * 0.2
      : 0.25 + Math.sin(t * 1.5 + a[0]) * 0.1;
  });
  return (
    <line ref={ref} geometry={geometry}>
      <lineBasicMaterial
        color={critical ? "#FFB300" : "#00E5FF"}
        transparent
        opacity={critical ? 0.85 : 0.3}
        linewidth={1}
      />
    </line>
  );
}

function NetworkGroup({ scrollRef }) {
  const groupRef = useRef();
  const innerRef = useRef();
  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const scroll = scrollRef?.current || 0; // 0..1
    groupRef.current.rotation.y = t * 0.12 + scroll * Math.PI * 0.6;
    groupRef.current.rotation.x = Math.sin(t * 0.25) * 0.08 + scroll * 0.25;
    groupRef.current.position.z = -scroll * 4;
    if (innerRef.current) {
      innerRef.current.position.y = Math.sin(t * 0.8) * 0.15;
      innerRef.current.rotation.z = Math.sin(t * 0.4) * 0.05;
    }
  });

  return (
    <group ref={groupRef}>
      <group ref={innerRef}>
        {EDGES.map(([i, j, critical], idx) => (
          <Edge key={idx} a={NODES[i].pos} b={NODES[j].pos} critical={critical} />
        ))}
        {NODES.map((n) => (
          <Node key={n.id} position={n.pos} critical={n.critical} />
        ))}
      </group>
    </group>
  );
}

function ParticleField() {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(300 * 3);
    for (let i = 0; i < 300; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 22;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 14;
    }
    return arr;
  }, []);
  useFrame((state) => {
    if (!ref.current) return;
    ref.current.rotation.y = state.clock.elapsedTime * 0.02;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial size={0.025} color="#4A6E8C" transparent opacity={0.6} />
    </points>
  );
}

export default function CriticalPathScene({ scrollRef }) {
  return (
    <Canvas
      data-testid="hero-3d-canvas"
      camera={{ position: [0, 0, 8], fov: 55 }}
      dpr={[1, 1.6]}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent" }}
    >
      <ambientLight intensity={0.5} />
      <ParticleField />
      <NetworkGroup scrollRef={scrollRef} />
    </Canvas>
  );
}
